import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useConversationById } from '@ltvco/refresh-lib/hooks';
import { useMediaQuery } from '@ltvco/refresh-lib/theme';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import {
  ChatInput,
  MessagesContainer,
  ImageUploadErrorAlert,
} from 'components/ChatBV/components';
import {
  INITIAL_SEARCH_KEY,
  LOADING_STATUS,
  PROCESSING_FLAG_KEY,
} from 'components/ChatBV/constants';
import { ChatContext, ChatLayoutContext } from 'components/ChatBV/context';
import { useChatForm } from 'components/ChatBV/hooks';
import { Message } from 'components/ChatBV/types';
import { mapConversationMessagesToMessages } from 'components/ChatBV/utils/conversationUtils';
import { extractSearchValueFromFirstRecord } from 'routes/utils/extractSearchValueFromFirstRecord';

export const ChatPage = () => {
  const {
    messages,
    loadingStatus,
    imageUploadError,
    setImageUploadError,
    setMessages,
    setContextId,
    sendMessage,
    generatedReports,
  } = useContext(ChatContext);
  const { logError } = useContext(AppConfig);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnChatBVFlowVariation = useFeatureIsOn('prop-3502');
  const { handleSendMessage } = useChatForm(() => {});
  const params = useParams();
  const contextId = params.context_id || params.contextId;
  const {
    showLanding,
    setShowLanding,
    setHasCompletedAnimation,
    isLogoAnimating,
    setIsLogoAnimating,
    logoAnimationPhase,
    setLogoAnimationPhase,
    reportState,
    setCloseAllPanels,
  } = useContext(ChatLayoutContext);
  const isConversationRoute = location.pathname.startsWith(
    '/chatbv/chat/conversation'
  );
  const isBaseChatRoute = location.pathname === '/chatbv/chat';

  // Helper function to check if we should process initial search
  const shouldProcessInitialSearch = useCallback((): boolean => {
    // Only process if user is in the prop-3502 variation
    if (!isOnChatBVFlowVariation) {
      return false;
    }

    const isOnChatRoute = isBaseChatRoute || isConversationRoute;
    const hasProcessingFlag = !!localStorage.getItem(PROCESSING_FLAG_KEY);

    return (
      isOnChatRoute &&
      !hasProcessingFlag &&
      messages.length === 0 &&
      loadingStatus === LOADING_STATUS.IDLE &&
      !!sendMessage
    );
  }, [
    isOnChatBVFlowVariation,
    isBaseChatRoute,
    isConversationRoute,
    messages.length,
    loadingStatus,
    sendMessage,
  ]);

  // Register closeAllPanels function in context so ChatLayoutProvider can use it
  useEffect(() => {
    setCloseAllPanels(() => {
      reportState.closeAllPanels();
    });
  }, [reportState.closeAllPanels, setCloseAllPanels]);

  const conversationQuery = useConversationById(contextId);
  const processedContextIdRef = useRef<string | null>(null);

  // Reset processed ref when contextId changes
  useEffect(() => {
    if (processedContextIdRef.current !== contextId) {
      processedContextIdRef.current = null;
    }
  }, [contextId]);

  useEffect(() => {
    // Early return if conditions are not met
    if (!shouldProcessInitialSearch()) {
      return;
    }

    // Check for existing processing flag
    const existingProcessingFlag = localStorage.getItem(PROCESSING_FLAG_KEY);
    if (existingProcessingFlag) {
      return; // Another execution is already processing
    }

    // Set processing flag IMMEDIATELY before reading data (atomic operation)
    // This prevents concurrent executions from proceeding
    const flagTimestamp = Date.now().toString();
    localStorage.setItem(PROCESSING_FLAG_KEY, flagTimestamp);

    // Read initial search data
    const initialSearchData = localStorage.getItem(INITIAL_SEARCH_KEY);

    if (!initialSearchData) {
      localStorage.removeItem(PROCESSING_FLAG_KEY);
      return;
    }

    try {
      const firstRecordSearchData = JSON.parse(initialSearchData);
      const searchQuery = extractSearchValueFromFirstRecord(
        firstRecordSearchData
      );

      if (searchQuery) {
        // Send message first
        sendMessage(searchQuery);

        // Remove data from localStorage immediately after sending message
        localStorage.removeItem(INITIAL_SEARCH_KEY);
        // Remove processing flag immediately - it already served its purpose
        localStorage.removeItem(PROCESSING_FLAG_KEY);
      } else {
        // If no search query, clean up immediately
        localStorage.removeItem(INITIAL_SEARCH_KEY);
        localStorage.removeItem(PROCESSING_FLAG_KEY);
      }
    } catch (error) {
      logError?.('Error parsing initialSearchData', error as Error);
      // Clean up on error
      localStorage.removeItem(INITIAL_SEARCH_KEY);
      localStorage.removeItem(PROCESSING_FLAG_KEY);
    }
  }, [shouldProcessInitialSearch, sendMessage, location.pathname, logError]);

  useEffect(() => {
    if (isBaseChatRoute && messages.length === 0) {
      reportState.closeAllPanels();
      // Clear processing flag when returning to landing page with no messages
      localStorage.removeItem(PROCESSING_FLAG_KEY);
    }
  }, [
    location.pathname,
    isBaseChatRoute,
    messages.length,
    reportState.closeAllPanels,
  ]);

  useEffect(() => {
    const { data: conversation } = conversationQuery;

    if (!contextId) {
      processedContextIdRef.current = null;
      return;
    }

    // Skip if we already processed this conversation
    if (processedContextIdRef.current === contextId) {
      return;
    }

    // Process the conversation data when it's available
    if (conversation && conversation.messages) {
      if (conversation.context_id !== contextId) {
        return;
      }

      const mappedMessages: Message[] = mapConversationMessagesToMessages(
        conversation.messages,
        generatedReports
      );
      setMessages(mappedMessages);
      setContextId(contextId);
      processedContextIdRef.current = contextId;

      // Hide landing page when loading an existing conversation
      if (mappedMessages.length > 0) {
        setShowLanding(false);
        setHasCompletedAnimation(true);
      }
    }
  }, [
    conversationQuery.data,
    contextId,
    generatedReports,
    setMessages,
    setContextId,
    setShowLanding,
    setHasCompletedAnimation,
  ]);

  // Use MUI's useMediaQuery with custom breakpoint to target exactly 1024px
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');

  const handleCloseImageUploadError = () => {
    setImageUploadError(null);
  };

  // Handle transition from landing page to chat
  useEffect(() => {
    if (messages.length > 0 && showLanding && !isConversationRoute) {
      const lastMessage = messages[messages.length - 1];
      const isUserMessage = lastMessage?.role === 'user';

      if (isUserMessage) {
        // Start logo animation
        setIsLogoAnimating(true);
        setLogoAnimationPhase('moving-up');

        // After logo moves up, move it to the left
        setTimeout(() => {
          setLogoAnimationPhase('moving-left');
        }, 800);

        // After logo moves left, transition to chat
        setTimeout(() => {
          setLogoAnimationPhase('header');
          setTimeout(() => {
            setHasCompletedAnimation(true);
            setShowLanding(false);
            setIsLogoAnimating(false);
            navigate(`/chatbv/chat/conversation${location.search}`, {
              replace: false,
            });
          }, 0);
        }, 1200);
      }
    } else if (messages.length === 0 && !showLanding) {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
    }
  }, [
    messages.length,
    showLanding,
    messages,
    isConversationRoute,
    navigate,
    setIsLogoAnimating,
    setLogoAnimationPhase,
    setHasCompletedAnimation,
    setShowLanding,
  ]);

  return (
    <>
      <MessagesContainer
        reportState={reportState}
        messages={messages}
        loadingStatus={loadingStatus}
        onReportStateChange={reportState?.setOpenReport}
        showLanding={showLanding}
        isLogoAnimating={isLogoAnimating}
        logoAnimationPhase={logoAnimationPhase}
        onSendMessage={handleSendMessage}
        sx={{
          ...(isMobileBreakpoint &&
            !reportState?.openReport && {
              paddingTop: '0',
            }),
        }}
      />
      {!reportState?.openReport && messages.length > 0 && !showLanding && (
        <>
          {imageUploadError && (
            <ImageUploadErrorAlert
              message={imageUploadError}
              onClose={handleCloseImageUploadError}
            />
          )}
          {isMobileBreakpoint && (
            <ChatInput
              isReportOpen={reportState?.openReport}
              isLanding={false}
              isAnimating={isLogoAnimating}
              animationPhase={logoAnimationPhase}
              isLoading={loadingStatus !== LOADING_STATUS.IDLE}
            />
          )}
        </>
      )}
    </>
  );
};
